/**
 * JoinPage - Allows participants to join a session via event code.
 *
 * Design: DENiZEN
 * - Soft rounded cards with subtle shadows
 * - Natural green + teal accents
 * - WCAG 2.1 AA compliant
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { GreenPageLayout } from '@/components/GreenPageLayout'
import type { GroundingImage } from '@/components/ScrollingPebbleBackground'
import { trackApiError } from '@/services/telemetry'

interface EventInfo {
  event_id: string
  title: string
  description: string | null
  location: string
  project_title: string | null
  blurb: string | null
  grounding_image_ids: string[]
  image_mode: string
  can_join: boolean
}

const API_BASE = (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? ''

export function JoinPage() {
  const { code } = useParams<{ code: string }>()
  const navigate = useNavigate()

  const [inputCode, setInputCode] = useState(code?.toUpperCase() || '')
  const [eventInfo, setEventInfo] = useState<EventInfo | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [bgImages, setBgImages] = useState<GroundingImage[]>([])

  const lookupEvent = useCallback(async (eventCode: string) => {
    setLoading(true)
    setError(null)
    setEventInfo(null)

    try {
      const response = await fetch(`${API_BASE}/api/v1/join/${eventCode.toUpperCase()}`)

      if (!response.ok) {
        if (response.status === 429) {
          setError('Too many attempts. Please wait a minute before trying again.')
        } else if (response.status === 404) {
          setError('Event not found. Please check the code and try again.')
        } else if (response.status === 422) {
          setError('Invalid event code format. Please check and try again.')
        } else {
          try {
            const errorData: { detail?: string } = await response.json() as { detail?: string }
            setError(errorData.detail ?? 'Failed to look up event. Please try again.')
          } catch (parseErr) {
            console.error(`[JoinPage] Failed to parse error response (HTTP ${response.status}):`, parseErr)
            setError('Failed to look up event. Please try again.')
          }
        }
        return
      }

      const data = await response.json() as EventInfo
      setEventInfo(data)
    } catch (err) {
      console.error('Event lookup failed:', err)
      // Classify errors for more helpful user feedback
      if (err instanceof TypeError) {
        // fetch() throws TypeError for network failures
        setError('Network error. Please check your connection and try again.')
      } else if (err instanceof SyntaxError) {
        // JSON parsing failed - server returned invalid response
        setError('Received an unexpected response. Please try again.')
      } else {
        setError('Something went wrong. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  // Look up event when code is provided via URL
  useEffect(() => {
    if (code && code.length === 6) {
      void lookupEvent(code)
    }
  }, [code, lookupEvent])

  // Seed background pebbles with community images on mount.
  useEffect(() => {
    const controller = new AbortController()
    fetch(`${API_BASE}/api/v1/images/random?count=6`, { signal: controller.signal })
      .then(r => {
        if (!r.ok) {
          console.warn(`[JoinPage] /api/v1/images/random returned HTTP ${r.status} — falling back to solid pebbles`)
          return Promise.resolve([])
        }
        return r.json() as Promise<GroundingImage[]>
      })
      .then(data => setBgImages(data))
      .catch(err => {
        if (err instanceof Error && err.name === 'AbortError') return
        console.warn('[JoinPage] Background image fetch failed — pebbles will use solid colours:', err)
        void trackApiError(err instanceof Error ? err : new Error(String(err)), '/api/v1/images/random')
      })
    return () => controller.abort()
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (inputCode.length === 6) {
      // Update URL and look up event
      void navigate(`/join/${inputCode.toUpperCase()}`, { replace: true })
      void lookupEvent(inputCode)
    }
  }

  const handleJoinSession = () => {
    if (!eventInfo) return
    // Pass event context via router state so SessionFlow attaches it to the session at consent time.
    // Session is created once (with grounding_image_ids) rather than twice (orphaned pre-created + new).
    void navigate('/session', {
      state: {
        fromEventCode: true,
        eventId: eventInfo.event_id,
        groundingImageIds: eventInfo.grounding_image_ids,
      },
    })
  }

  return (
    <GreenPageLayout images={bgImages}>
      <div className="overflow-y-auto flex-1 px-8 py-10 sm:px-12">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="/images/brand/DenizenLogoLarge.png"
            alt="DENiZEN"
            className="w-32 mx-auto mb-4"
          />
          <h1 className="font-display text-4xl font-semibold text-dark-green tracking-tight mb-4">
            Join Session
          </h1>
          <p className="font-body text-lg text-muted">
            Enter your session code to get started
          </p>
        </div>

        {/* Code entry form */}
        {!eventInfo && (
          <form onSubmit={handleSubmit} className="p-8 mb-6 border border-dark-green/10 rounded-2xl bg-white/40">
            <label htmlFor="session-code" className="block font-body text-sm font-semibold tracking-wide text-dark-green mb-3">
              Session Code
            </label>
            <div className="flex gap-4">
              <input
                id="session-code"
                type="text"
                value={inputCode}
                onChange={(e) => setInputCode(e.target.value.toUpperCase())}
                placeholder="ABC123"
                maxLength={6}
                className="flex-1 px-6 py-4 font-mono text-2xl tracking-widest text-center uppercase border-2 border-dark-green/15 rounded-2xl bg-white focus:outline-none focus:ring-4 focus:ring-mid-green"
                autoFocus
                autoComplete="off"
              />
              <button
                type="submit"
                disabled={inputCode.length !== 6 || loading}
                className="px-8 py-4 font-body font-semibold tracking-wide bg-dark-green text-white border-2 border-dark-green/15 rounded-2xl shadow-denizen transition-all duration-150 hover:shadow-denizen hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:translate-y-0"
              >
                {loading ? 'Looking...' : 'Find'}
              </button>
            </div>
          </form>
        )}

        {/* Error message */}
        {error && (
          <div className="p-4 border border-error/30 bg-error/10 rounded-2xl mb-6" role="alert" aria-live="assertive">
            <p className="font-body text-dark-green">{error}</p>
            {eventInfo === null && (
              <button
                type="button"
                onClick={() => {
                  setError(null)
                  setInputCode('')
                }}
                className="mt-2 font-body text-sm underline hover:no-underline"
              >
                Try a different code
              </button>
            )}
          </div>
        )}

        {/* Event info */}
        {eventInfo && (
          <div className="animate-slide-up">
            <div className="flex items-start justify-between mb-6">
              <div>
                {eventInfo.project_title && (
                  <p className="font-body text-xs font-semibold tracking-wide text-muted mb-1">
                    {eventInfo.project_title}
                  </p>
                )}
                {!eventInfo.project_title && (
                  <p className="font-body text-xs font-semibold tracking-wide text-muted mb-1">
                    Event Found
                  </p>
                )}
                <h2 className="font-display text-3xl font-semibold text-dark-green">
                  {eventInfo.title}
                </h2>
              </div>
              <code className="px-3 py-2 bg-light-green border border-dark-green/15 rounded-2xl font-mono text-lg">
                {inputCode}
              </code>
            </div>

            {eventInfo.description && (
              <p className="font-body text-muted mb-6">
                {eventInfo.description}
              </p>
            )}

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 border border-dark-green/15 rounded-2xl bg-white/40">
                <p className="font-body text-xs font-semibold tracking-wide text-muted mb-1">
                  Location
                </p>
                <p className="font-body text-dark-green">{eventInfo.location}</p>
              </div>
              {eventInfo.image_mode && (
                <div className="p-4 border border-dark-green/15 rounded-2xl bg-white/40">
                  <p className="font-body text-xs font-semibold tracking-wide text-muted mb-1">
                    Image Mode
                  </p>
                  <p className="font-body text-dark-green capitalize">{eventInfo.image_mode}</p>
                </div>
              )}
            </div>

            {/* Blurb */}
            {eventInfo.blurb && (
              <div className="p-4 border border-dark-green/15 rounded-2xl bg-white/40">
                <p className="font-body text-dark-green whitespace-pre-line">{eventInfo.blurb}</p>
              </div>
            )}

            {/* Join button or status message */}
            {eventInfo.can_join ? (
              <button
                type="button"
                onClick={handleJoinSession}
                className="w-full py-4 font-display text-xl font-semibold tracking-wide bg-dark-green text-white border-2 border-dark-green/15 rounded-2xl shadow-denizen transition-all duration-150 hover:shadow-denizen hover:-translate-y-1"
              >
                <span className="flex items-center justify-center gap-2">
                  Join Session
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </button>
            ) : (
              <div className="p-4 border border-dark-green/15 rounded-2xl bg-white/40 text-center">
                <p className="font-body text-dark-green">This event is not currently accepting new participants.</p>
              </div>
            )}

            {/* Different session link */}
            <button
              type="button"
              onClick={() => {
                setEventInfo(null)
                setInputCode('')
                setError(null)
                void navigate('/join', { replace: true })
              }}
              className="w-full mt-4 py-3 font-body text-sm tracking-wide text-muted hover:text-dark-green border border-transparent hover:border-dark-green/15 rounded-2xl transition-all"
            >
              Use a different code
            </button>
          </div>
        )}
      </div>
    </GreenPageLayout>
  )
}

export default JoinPage
